<!--
  An ElAutocomplete specialized for Entities and Synonynms
-->
<template>
  <div
    class="el-autocomplete el-entity-autocomplete"
    v-clickoutside="close"
    aria-haspopup="listbox"
    role="combobox"
    :aria-expanded="suggestionVisible"
    :aria-owns="id"
  >
    <el-entity-input
      ref="input"
      v-bind="[$props, $attrs]"
      :prefix="entityPrefix"
      :class-name="entityClass"
      :style-obj="entityStyle"
      @input="handleChange"
      @focus="handleFocus"
      @blur="handleBlur"
      @clear="handleClear"
      @keydown.up.native.prevent="highlight(highlightedIndex - 1)"
      @keydown.down.native.prevent="highlight(highlightedIndex + 1)"
      @keydown.enter.native="handleKeyEnter"
      @keydown.native.tab="close"
    >
      <template slot="prepend" v-if="$slots.prepend">
        <slot name="prepend"></slot>
      </template>
      <template slot="append" v-if="$slots.append">
        <slot name="append"></slot>
      </template>
      <template slot="prefix" v-if="$slots.prefix">
        <slot name="prefix"></slot>
      </template>
      <template slot="suffix" v-if="$slots.suffix">
        <slot name="suffix"></slot>
      </template>
    </el-entity-input>
    <el-mobile-suggestions
      :visible-arrow="true"
      :class="[popperClass ? popperClass : '']"
      :popper-options="popperOptions"
      :append-to-body="popperAppendToBody"
      ref="suggestions"
      :placement="placement"
      :id="id">
      <li
        v-for="(item, index) in querySuggestions"
        :key="itemKey(item, index)"
        :class="{'highlighted': highlightedIndex === index}"
        @click="select(item)"
        :id="`${id}-item-${index}`"
        role="option"
        :aria-selected="highlightedIndex === index"
      >
        <slot :item="item">
          <span :class="['el-entity-autocomplete__item', itemClass(item)]" :style="itemStyle(item)">
            <span class="el-entity-autocomplete__prefix">{{ entityPrefix
            }}</span><span class="el-entity-autocomplete__left">{{ item.left
            }}</span><strong class="el-entity-autocomplete__match">{{ item.match
            }}</strong><span class="el-entity-autocomplete__right">{{ item.right
            }}</span>
          </span>
          <span class="el-entity-autocomplete__count"
            v-if="'count' in item"> ({{item.count}})</span>
        </slot>
      </li>
    </el-mobile-suggestions>
  </div>
</template>
<script>
import debounce from 'throttle-debounce/debounce'
import ElEntityInput from './ElEntityInput.vue'
import Clickoutside from 'element-ui/lib/utils/clickoutside'
import ElMobileSuggestions from './ElMobileSuggestions.vue'
import Emitter from 'element-ui/lib/mixins/emitter'
import { generateId } from 'element-ui/lib/utils/util'
import Focus from 'element-ui/lib/mixins/focus'
import { filterSuggestions } from '../../utils/filter.js'
import { getValueByPath } from 'element-ui/src/utils/util'

export default {
  name: 'ElEntityAutocomplete',

  mixins: [Emitter, Focus('input')],

  inheritAttrs: false,

  componentName: 'ElAutocomplete',

  components: {
    ElEntityInput,
    ElMobileSuggestions,
  },

  directives: { Clickoutside },

  props: {
    valueKey: {
      type: String,
      default: 'value',
    },
    labelKey: {
      type: String,
      default: null,
    },
    classKey: {
      type: String,
      default: 'class',
    },
    styleKey: {
      type: String,
      default: 'style',
    },
    popperClass: String,
    popperOptions: Object,
    placeholder: String,
    clearable: {
      type: Boolean,
      default: false,
    },
    disabled: Boolean,
    name: String,
    size: String,
    value: String,
    maxlength: Number,
    minlength: Number,
    autofocus: Boolean,
    // fetchSuggestions: Function,
    triggerOnFocus: {
      type: Boolean,
      default: true,
    },
    customItem: String,
    selectWhenUnmatched: {
      type: Boolean,
      default: false,
    },
    prefixIcon: String,
    suffixIcon: String,
    label: String,
    debounce: {
      type: Number,
      default: 300,
    },
    placement: {
      type: String,
      default: 'bottom-start',
    },
    hideLoading: Boolean,
    popperAppendToBody: {
      type: Boolean,
      default: true,
    },
    // the type of entity, changes its style
    type: String,
    // the prefix to show
    prefix: String,
    // the class to use
    className: String,
    // the style to apply
    styleObj: Object,
    // more direct suggestion system
    suggestions: Array,

    keyField: {
      type: String,
      default: null,
    },
    valueField: {
      type: String,
      default: 'value',
    },
    labelField: {
      type: String,
      default: null,
    },
    classField: {
      type: String,
      default: 'class',
    },
    styleField: {
      type: String,
      default: 'style',
    },
  },
  data () {
    return {
      activated: false,
      querySuggestions: [],
      loading: false,
      highlightedIndex: -1,
      suggestionDisabled: false,
    }
  },
  computed: {
    suggestionVisible () {
      const suggestions = this.querySuggestions
      let isValidData = Array.isArray(suggestions) && suggestions.length > 0
      return (isValidData || this.loading) && this.activated
    },
    id () {
      return `el-autocomplete-${generateId()}`
    },
    // used for ofr styling suggestions
    selectedSuggestion () {
      let item = this.suggestions.find(item => this.itemValue(item) === this.value)
      if (item && this.itemObject(item)) return item
      else return null
    },
    entityPrefix () {
      if (this.prefix) return this.prefix
      else if (this.type === 'entity') return '@'
      else if (this.type === 'synonym') return '#'
      else if (this.type === 'variable') return '$'
      else return ''
    },
    entityClass () {
      return this.className ||
        this.classField && this.selectedSuggestion &&
        getValueByPath(this.selectedSuggestion, this.classField) ||
        this.type && 'el-entity-autocomplete__item-' + this.type || ''
    },
    entityStyle () {
      return this.styleObj || this.styleField && this.selectedSuggestion &&
        getValueByPath(this.selectedSuggestion, this.styleField) || null
    },
  },
  watch: {
    suggestionVisible (val) {
      this.broadcast('ElAutocompleteSuggestions', 'visible', [val, this.$refs.input.$refs.input.offsetWidth])
    },
  },
  methods: {
    itemObject (item) {
      return Object.prototype.toString.call(item).toLowerCase() === '[object object]'
    },
    itemKey (item, index) {
      let keyField = this.keyField || this.valueField
      if (!this.itemObject(item)) return item
      else if (keyField) return getValueByPath(item, keyField)
      else return index
    },
    itemValue (item) {
      if (!this.valueField || !this.itemObject(item)) return item
      return getValueByPath(item, this.valueField)
    },
    itemLabel (item) {
      let labelField = this.labelField || this.valueField
      if (!this.itemObject(item)) return item
      else if (labelField) return getValueByPath(item, labelField)
      else return ''
    },
    itemClass (item) {
      return this.classField && this.itemObject(item) &&
        getValueByPath(item, this.classField) ||
        this.className || this.type && 'el-entity-autocomplete__item-' + this.type || ''
    },
    itemStyle (item) {
      return this.styleField && this.itemObject(item) &&
        getValueByPath(item, this.styleField) ||
        this.styleObj
    },

    getData (queryString) {
      if (this.suggestionDisabled) {
        return
      }
      // more simple fetch
      this.querySuggestions = filterSuggestions(queryString, this.suggestions || [], this.labelKey || this.valueKey)
      // this.loading = true;
      // this.fetchSuggestions(queryString, (suggestions) => {
      //   this.loading = false;
      //   if (this.suggestionDisabled) {
      //     return;
      //   }
      //   if (Array.isArray(suggestions)) {
      //     this.suggestions = suggestions;
      //   } else {
      //     console.error('[Element Error][Autocomplete]autocomplete suggestions must be an array');
      //   }
      // });
    },
    handleChange (value) {
      this.$emit('input', value)
      this.suggestionDisabled = false
      if (!this.triggerOnFocus && !value) {
        this.suggestionDisabled = true
        this.querySuggestions = []
        return
      }
      this.debouncedGetData(value)
    },
    handleFocus (event) {
      this.activated = true
      this.$emit('focus', event)
      if (this.triggerOnFocus) {
        this.debouncedGetData(this.value)
      }
    },
    handleBlur (event) {
      this.$emit('blur', event)
    },
    handleClear () {
      this.activated = false
      this.$emit('clear')
    },
    close (e) {
      this.activated = false
    },
    handleKeyEnter (e) {
      if (this.suggestionVisible && this.highlightedIndex >= 0 && this.highlightedIndex < this.querySuggestions.length) {
        e.preventDefault()
        this.select(this.querySuggestions[this.highlightedIndex])
      } else if (this.selectWhenUnmatched) {
        this.$emit('select', { value: this.value })
        this.$nextTick(_ => {
          this.querySuggestions = []
          this.highlightedIndex = -1
        })
      }
    },
    select (item) {
      this.$emit('input', item[this.valueKey])
      this.$emit('select', item)
      this.$nextTick(_ => {
        this.querySuggestions = []
        this.highlightedIndex = -1
      })
    },
    highlight (index) {
      if (!this.suggestionVisible || this.loading) { return }
      if (index < 0) {
        this.highlightedIndex = -1
        return
      }
      if (index >= this.querySuggestions.length) {
        index = this.querySuggestions.length - 1
      }
      const suggestion = this.$refs.suggestions.$el.querySelector('.el-autocomplete-suggestion__wrap')
      const suggestionList = suggestion.querySelectorAll('.el-autocomplete-suggestion__list li')

      let highlightItem = suggestionList[index]
      let scrollTop = suggestion.scrollTop
      let offsetTop = highlightItem.offsetTop

      if (offsetTop + highlightItem.scrollHeight > (scrollTop + suggestion.clientHeight)) {
        suggestion.scrollTop += highlightItem.scrollHeight
      }
      if (offsetTop < scrollTop) {
        suggestion.scrollTop -= highlightItem.scrollHeight
      }
      this.highlightedIndex = index
      this.$el.querySelector('.el-input__inner').setAttribute('aria-activedescendant', `${this.id}-item-${this.highlightedIndex}`)
    },
  },
  mounted () {
    this.debouncedGetData = debounce(this.debounce, this.getData)
    this.$on('item-click', item => {
      this.select(item)
    })
    let $input = this.$el.querySelector('.el-input__inner')
    $input.setAttribute('role', 'textbox')
    $input.setAttribute('aria-autocomplete', 'list')
    $input.setAttribute('aria-controls', 'id')
    $input.setAttribute('aria-activedescendant', `${this.id}-item-${this.highlightedIndex}`)
  },
  beforeDestroy () {
    this.$refs.suggestions.$destroy()
  },
}
</script>

<style lang="scss">
  @import "../../element-variables.scss";

  /* avoid weird CSS bugs with height */
  .el-autocomplete.el-entity-autocomplete {
    height: $--input-height;
  }
  /* style of entities */
  .el-entity-autocomplete__item-entity {
    background-color: $entity-bg-color;
  }
  .el-entity-autocomplete__item-synonym {
    background-color: $synonym-bg-color;
  }
  .el-entity-autocomplete__item-variable {
    background-color: $variable-bg-color;
  }
  .el-entity-autocomplete__count {
    color: #d5c9cd;
  }
</style>
